import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ClinicalTeam from '../components/ClinicalTeam';

function ImageWithFallback(props: any) {
  const [didError, setDidError] = useState(false)
  const { src, alt, style, className, ...rest } = props

  return didError ? (
    <div
      className={`inline-block bg-gray-100 text-center align-middle ${className ?? ''}`}
      style={style}
    >
      <div className="flex items-center justify-center w-full h-full">
        <img src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODgiIGhlaWdodD0iODgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgc3Ryb2tlPSIjMDAwIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBvcGFjaXR5PSIuMyIgZmlsbD0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIzLjciPjxyZWN0IHg9IjE2IiB5PSIxNiIgd2lkdGg9IjU2IiBoZWlnaHQ9IjU2IiByeD0iNiIvPjxwYXRoIGQ9Im0xNiA1OCAxNi0xOCAzMiAzMiIvPjxjaXJjbGUgY3g9IjUzIiBjeT0iMzUiIHI9IjciLz48L3N2Zz4KCg==" alt="Error loading image" {...rest} data-original-url={src} />
      </div>
    </div>
  ) : (
    <img src={src} alt={alt} className={className} style={style} {...rest} onError={() => setDidError(true)} />
  )
}

interface NonClinicalMemberProps {
  id: number;
  name: string;
  title: string;
  company: string;
  profileImage: string;
  buttonText?: string;
}

const NonClinicalMember = ({ id, name, title, company, profileImage, buttonText = "Know More" }: NonClinicalMemberProps) => {
  const navigate = useNavigate();
  
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg hover:scale-105">
      <div className="flex flex-col items-center p-4">
        <div className="w-24 h-24 rounded-full overflow-hidden mb-4 border-2 border-green-100">
          <ImageWithFallback
            src={profileImage}
            alt={`${name}'s profile`}
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="text-center mb-4">
          <h3 className="font-semibold text-lg text-gray-800">{name}</h3>
          <p className="text-gray-600 text-sm">{title}</p>
          <p className="text-gray-500 text-xs">{company}</p>
        </div>
        
        <button 
          onClick={() => navigate(`/team/${id}`)}
          className="px-4 py-2 text-white rounded-full text-sm font-medium transition-colors w-full" 
          style={{ backgroundColor: '#22c55e' }} 
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#16a34a'} 
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#22c55e'}
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
};

const NonClinicalTeam = () => {
  const nonClinicalMembers = [
    {
      id: 1,
      name: "Mr. Cletus Osigwe",
      title: "Accountant & Family Budgeting Trainer",
      company: "HealthCity UK",
      profileImage: require("../assets/cletus-6pKJkpiW.jpg")
    },
    {
      id: 2,
      name: "Afroz Kazim Ahmed",
      title: "Senior Data Manager at National Health Service (NHS) in the UK",
      company: "HealthCity UK",
      profileImage: require("../assets/mark-EDd2Rsg-.jpg")
    },
    {
      id: 3,
      name: "Dr Robert Patton, PhD",
      title: "Research & Development Governance Lead/Advisor",
      company: "HealthCity UK",
      profileImage: require("../assets/robert-d2dd_oBP.jpg")
    },
    {
      id: 4,
      name: "Theresa Seikegba",
      title: "Business Development Coordinator & Global Cookery Expert",
      company: "HealthCity UK",
      profileImage: require("../assets/seikegba-wXoIwB0r.jpg")
    },
    {
      id: 5,
      name: "Mrs Onyinye Charity Oluitan",
      title: "Operations Manager, Client/Training Services & Management Consultant",
      company: "HealthCity UK",
      profileImage: require("../assets/onyinye-Xt-Cq9c9.jpg")
    }
  ];

  return (
    <div className="w-full bg-green-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <header className="mb-10">
          <h2 className="text-center text-2xl md:text-3xl font-bold">
            <span className="text-gray-400">NON-CLINICAL</span>
            <span className="text-gray-800"> TEAM</span>
          </h2>
          <div className="w-24 h-1 bg-green-500 mx-auto mt-3"></div>
        </header>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {nonClinicalMembers.map(member => (
            <NonClinicalMember
              key={member.id}
              id={member.id}
              name={member.name}
              title={member.title}
              company={member.company}
              profileImage={member.profileImage}
              buttonText="Know More"
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default function TeamsPage() {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<'clinical' | 'non-clinical'>(
    tabParam === 'non-clinical' ? 'non-clinical' : 'clinical'
  );

  useEffect(() => {
    if (tabParam === 'non-clinical') {
      setActiveTab('non-clinical');
    } else if (tabParam === 'clinical') {
      setActiveTab('clinical');
    }
  }, [tabParam]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Teams</h1>
          <p className="text-lg text-gray-600 max-w-3xl">
            Meet the dedicated professionals who make Health City a leading healthcare institution. 
            Our teams combine clinical expertise with administrative excellence to provide exceptional care.
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('clinical')}
              className={`py-4 px-2 border-b-2 font-medium text-sm ${
                activeTab === 'clinical'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Clinical Team
            </button>
            <button
              onClick={() => setActiveTab('non-clinical')}
              className={`py-4 px-2 border-b-2 font-medium text-sm ${
                activeTab === 'non-clinical'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Non-Clinical Team
            </button>
          </div>
        </div>
      </div>

      {/* Team Content */}
      <div className="py-8">
        {activeTab === 'clinical' && <ClinicalTeam />}
        {activeTab === 'non-clinical' && <NonClinicalTeam />}
      </div>
    </div>
  );
}